import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { eventQuotesAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Loader2, CheckCircle2, Calendar, Users, Shield, Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { siteConfig } from '../config/site';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_caredata-suite/artifacts/8itk90ub_55315630-dcf1-43bd-9c0d-4f1818f5e217.jpeg";

const ATTENDEE_OPTIONS = ['500', '1,000', '2,000', '5,000', '10,000', '20,000'];
const EVENT_TYPES = [
  'Concert',
  'Sporting Event',
  'Festival',
  'Corporate Event',
  'Private Event',
  'Marathon/Run',
  'Conference',
  'Other'
];
const COVERAGE_LEVELS = [
  'Basic First Aid',
  'BLS (Basic Life Support)',
  'ALS (Advanced Life Support)',
  'Ambulance Standby',
  'Doctor-Led Medical Team',
  'Full Field Hospital'
];

export default function EventQuotePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    event_location: '',
    event_dates: '',
    event_duration: '',
    expected_attendees: '',
    event_type: '',
    coverage_level: '',
    additional_notes: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const required = ['business_name', 'contact_name', 'email', 'phone', 'event_location', 'event_dates', 'event_duration', 'expected_attendees', 'event_type', 'coverage_level'];
    const missing = required.filter(f => !formData[f]);
    if (missing.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await eventQuotesAPI.submit(formData);
      setReferenceNumber(response.data.reference_number);
      setSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit quote request');
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-lg text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Quote Request Submitted!</h2>
            <p className="text-muted-foreground mb-6">
              Thank you for your interest in Apex Medical Group's event medical services.
              Our team will review your request and contact you within 24-48 hours.
            </p>
            <div className="bg-muted p-4 rounded-lg mb-6">
              <p className="text-sm text-muted-foreground mb-1">Your Reference Number</p>
              <p className="text-2xl font-mono font-bold">{referenceNumber}</p>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Please save this reference number for your records.
            </p>
            <Button onClick={() => navigate('/login')} className="w-full">
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="event-quote-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/login" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Link>
          <img 
            src={LOGO_URL} 
            alt="Apex Medical Group" 
            className="w-20 h-20 mx-auto mb-4 rounded-xl object-contain"
          />
          <h1 className="text-3xl font-bold tracking-tight">Event Medical Services</h1>
          <p className="text-muted-foreground mt-2">
            Professional medical coverage for events of all sizes
          </p>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="text-center p-4">
            <Shield className="w-8 h-8 mx-auto mb-2 text-primary" />
            <h3 className="font-semibold text-sm">Licensed Medical Teams</h3>
            <p className="text-xs text-muted-foreground">EMTs, Paramedics & Physicians</p>
          </Card>
          <Card className="text-center p-4">
            <Users className="w-8 h-8 mx-auto mb-2 text-primary" />
            <h3 className="font-semibold text-sm">All Event Sizes</h3>
            <p className="text-xs text-muted-foreground">From 500 to 20,000+ attendees</p>
          </Card>
          <Card className="text-center p-4">
            <Calendar className="w-8 h-8 mx-auto mb-2 text-primary" />
            <h3 className="font-semibold text-sm">Flexible Coverage</h3>
            <p className="text-xs text-muted-foreground">Single day or multi-day events</p>
          </Card>
        </div>

        {/* Quote Form */}
        <Card>
          <CardHeader>
            <CardTitle>Request a Quote</CardTitle>
            <CardDescription>
              Fill out the form below and our team will provide a customized quote for your event.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business Information */}
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Business Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">Business/Organization Name *</Label>
                    <Input
                      id="business_name"
                      value={formData.business_name}
                      onChange={(e) => handleChange('business_name', e.target.value)}
                      placeholder="Company Name"
                      required
                      data-testid="business-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Person *</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => handleChange('contact_name', e.target.value)}
                      placeholder="Your Name"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="you@company.com"
                      required
                      data-testid="email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="+1 (758) 000-0000"
                      required
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Event Details */}
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Event Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="event_location">Event Location *</Label>
                    <Input
                      id="event_location"
                      value={formData.event_location}
                      onChange={(e) => handleChange('event_location', e.target.value)}
                      placeholder="Venue name, address, city"
                      required
                      data-testid="location-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event_dates">Event Date(s) *</Label>
                    <Input
                      id="event_dates"
                      value={formData.event_dates}
                      onChange={(e) => handleChange('event_dates', e.target.value)}
                      placeholder="e.g., June 15-17, 2025"
                      required
                      data-testid="dates-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event_duration">Duration *</Label>
                    <Input
                      id="event_duration"
                      value={formData.event_duration}
                      onChange={(e) => handleChange('event_duration', e.target.value)}
                      placeholder="e.g., 8 hours/day"
                      required
                      data-testid="duration-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expected_attendees">Expected Attendees *</Label>
                    <Select 
                      value={formData.expected_attendees} 
                      onValueChange={(v) => handleChange('expected_attendees', v)}
                    >
                      <SelectTrigger data-testid="attendees-select">
                        <SelectValue placeholder="Select expected attendance" />
                      </SelectTrigger>
                      <SelectContent>
                        {ATTENDEE_OPTIONS.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event_type">Event Type *</Label>
                    <Select 
                      value={formData.event_type} 
                      onValueChange={(v) => handleChange('event_type', v)}
                    >
                      <SelectTrigger data-testid="event-type-select">
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                      <SelectContent>
                        {EVENT_TYPES.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Coverage Requirements */}
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Medical Coverage Requirements
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="coverage_level">Level of Coverage Requested *</Label>
                    <Select 
                      value={formData.coverage_level} 
                      onValueChange={(v) => handleChange('coverage_level', v)}
                    >
                      <SelectTrigger data-testid="coverage-select">
                        <SelectValue placeholder="Select coverage level" />
                      </SelectTrigger>
                      <SelectContent>
                        {COVERAGE_LEVELS.map(level => (
                          <SelectItem key={level} value={level}>{level}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="additional_notes">Additional Notes / Special Risks</Label>
                    <Textarea
                      id="additional_notes"
                      value={formData.additional_notes}
                      onChange={(e) => handleChange('additional_notes', e.target.value)}
                      placeholder="Please mention any special risks: alcohol service, motorsport, water events, extreme heat, pyrotechnics, mosh pits, etc."
                      rows={4}
                      data-testid="notes-input"
                    />
                  </div>
                </div>
              </div>

              <Alert>
                <AlertDescription>
                  By submitting this form, you agree to be contacted by Apex Medical Group regarding your event medical services inquiry.
                </AlertDescription>
              </Alert>

              <Button type="submit" className="w-full" disabled={loading} data-testid="submit-quote-btn">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit Quote Request
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="mt-6">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">Need Immediate Assistance?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{siteConfig.contact.phone.display}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{siteConfig.contact.email.primary}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
